import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import DocumentManager from "../components/DocumentManager";
import LegalRepresentatives from "../components/LegalRepresentatives";
import BeneficialOwnersList from "../components/BeneficialOwnersList";
import ComplianceReadiness from "../components/ComplianceReadiness";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import {
  ArrowLeft, CheckCircle, Shield, AlertTriangle, FileText, Upload,
  ArrowLeftRight, Search, ShieldCheck, ShieldAlert, Pencil, Save, X,
  CircleDot, Briefcase, Trash2, CheckCircle2, Loader2, FileDown, Download, RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const kycBadge = {
  draft: { label: "Borrador", cls: "bg-slate-500/10 text-slate-400 border border-slate-500/20", icon: CircleDot },
  pending: { label: "Pendiente", cls: "bg-amber-500/10 text-amber-400 border border-amber-500/20", icon: AlertTriangle },
  pending_docs: { label: "Pendiente Documentos", cls: "bg-blue-500/10 text-blue-400 border border-blue-500/20", icon: Upload },
  verified: { label: "Verificado", cls: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20", icon: CheckCircle },
  rejected: { label: "Rechazado", cls: "bg-red-500/10 text-red-400 border border-red-500/20", icon: AlertTriangle },
};
const riskBadge = {
  low: { label: "Bajo", cls: "bg-emerald-500/10 text-emerald-400" },
  medium: { label: "Medio", cls: "bg-amber-500/10 text-amber-400" },
  high: { label: "Alto", cls: "bg-orange-500/10 text-orange-400" },
  critical: { label: "Critico", cls: "bg-red-500/10 text-red-400" },
};

const REQUIRED_FISICA = ["first_name", "paternal_surname", "rfc", "curp", "nationality", "birth_date", "id_type", "id_number", "street", "state", "municipality", "phone", "email"];
const REQUIRED_MORAL = ["legal_name", "rfc", "street", "state", "municipality", "phone", "email"];

function computeCompleteness(customer) {
  if (!customer) return { pct: 0, missing: [], filled: 0, total: 0 };
  const required = customer.person_type === "moral" ? REQUIRED_MORAL : REQUIRED_FISICA;
  const missing = required.filter(f => !customer[f] || customer[f].toString().trim() === "");
  const filled = required.length - missing.length;
  return { pct: Math.round((filled / required.length) * 100), missing, filled, total: required.length };
}

const FIELD_LABELS = {
  first_name: "Nombre(s)", paternal_surname: "Apellido Paterno", maternal_surname: "Apellido Materno",
  legal_name: "Razon Social", commercial_name: "Nombre Comercial",
  birth_date: "Fecha de Nacimiento", nationality: "Nacionalidad", gender: "Genero",
  occupation: "Ocupacion", economic_activity: "Actividad Economica",
  curp: "CURP", rfc: "RFC", id_type: "Tipo de Identificacion",
  id_number: "No. Identificacion", id_expiry_date: "Vigencia ID",
  street: "Calle", ext_number: "No. Exterior", int_number: "No. Interior",
  neighborhood: "Colonia", zip_code: "C.P.", state: "Estado", municipality: "Municipio",
  phone: "Telefono", mobile: "Celular", email: "Email",
  is_pep: "PEP", pep_details: "Detalle PEP",
};

// CURP Status Card Component
function CurpStatusCard({ customer, onRetry }) {
  const [loading, setLoading] = useState(false);
  const [serviceError, setServiceError] = useState(null); // { message, screenshot }
  const API = import.meta.env.VITE_API_URL || "";

  const handleRetry = async () => {
    setLoading(true);
    setServiceError(null);
    try {
      await onRetry((result) => {
        // onRetry calls this callback with the raw API response when there's a failure
        if (result?.error_type === "service_unavailable") {
          setServiceError({ message: result.error, screenshot: result.screenshot });
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Success state - hide card completely (no permanent message)
  if (customer.curp_pdf_status === 'success' || customer.curp_pdf_status === 'downloaded' || customer.curp_pdf_status === 'uploaded_manual' || customer.curp_validated) {
    return null; // No mostrar card cuando ya está validada/cargada
  }

  // Service unavailable state — show gob.mx message + screenshot as evidence
  if (serviceError) {
    return (
      <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-4 mb-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="bg-amber-500/10 p-2 rounded-lg shrink-0 mt-0.5">
            <AlertTriangle size={18} className="text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-300">Servicio de gob.mx no disponible</p>
            <p className="text-xs text-amber-400/80 mt-0.5">{serviceError.message}</p>
            <p className="text-xs text-slate-500 mt-1">
              Este es un problema del sitio de gobierno, no de Notalia. Intenta más tarde.
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleRetry}
            disabled={loading}
            variant="outline"
            className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 shrink-0"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : "Reintentar"}
          </Button>
        </div>
        {serviceError.screenshot && (
          <div className="rounded-lg overflow-hidden border border-amber-500/20">
            <p className="text-xs text-slate-500 px-3 py-1.5 bg-slate-900/50">
              Evidencia — captura del sitio gob.mx al momento del intento
            </p>
            <img
              src={`${API}${serviceError.screenshot}`}
              alt="Captura gob.mx error"
              className="w-full max-h-64 object-top object-cover"
            />
          </div>
        )}
      </div>
    );
  }

  // Failed state (other errors)
  if (customer.curp_pdf_status === 'failed') {
    return (
      <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="bg-amber-500/10 p-2 rounded-lg shrink-0 mt-0.5">
            <AlertTriangle size={18} className="text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-300">No se pudo obtener la constancia CURP automáticamente</p>
            <p className="text-xs text-slate-400 mt-1">
              {customer.curp_pdf_error || "El sistema intentó descargar la constancia desde gob.mx pero no fue posible."}
            </p>
            <p className="text-xs text-teal-400 mt-2">
              Por favor, descarga la constancia manualmente desde <a href="https://www.gob.mx/curp/" target="_blank" rel="noopener noreferrer" className="underline hover:text-teal-300">gob.mx/curp</a> y súbela en la sección de Documentos.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Pending/no attempt state (cuando nunca se ha intentado descargar)
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-4">
      <div className="flex items-center gap-3">
        <div className="bg-slate-700/50 p-2 rounded-lg shrink-0">
          <FileDown size={18} className="text-slate-400" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-200">Constancia CURP</p>
          <p className="text-xs text-slate-400 mt-0.5">
            Descarga la constancia oficial manualmente desde <a href="https://www.gob.mx/curp/" target="_blank" rel="noopener noreferrer" className="underline hover:text-teal-300 text-teal-400">gob.mx/curp</a> y súbela en Documentos
          </p>
        </div>
      </div>
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState(null);
  const [risk, setRisk] = useState(null);
  const [owners, setOwners] = useState([]);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("info");
  const [screening, setScreening] = useState(null);
  const [screeningStatus, setScreeningStatus] = useState(null);
  const [screeningLoading, setScreeningLoading] = useState(false);
  // Phase index for the dedicated screening progress overlay. Drives the
  // "Validando…" → "Consultando OpenSanctions…" → "Consultando OFAC SDN…"
  // → "Procesando coincidencias…" sequence so the user has visible progress
  // during the ~5-30s call instead of staring at a single spinner.
  const [screeningPhase, setScreeningPhase] = useState(0);
  const [dismissModal, setDismissModal] = useState({ open: false, matchIndex: null, match: null, action: 'dismiss' });
  const [dismissReason, setDismissReason] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [verifyingKyc, setVerifyingKyc] = useState(false);
  const [verifyProgress, setVerifyProgress] = useState(null);
  const curpPollRef = useRef(null);

  const fetchCustomer = async () => {
    try {
      const [cRes, oRes, opRes] = await Promise.all([
        axios.get(`${API}/customers/${id}`, { withCredentials: true }),
        axios.get(`${API}/beneficial-owners?customer_id=${id}`, { withCredentials: true }),
        axios.get(`${API}/operations?customer_id=${id}`, { withCredentials: true }),
      ]);
      setCustomer(cRes.data);
      setOwners(oRes.data.items);
      setOperations(opRes.data.items);
      
      // Fetch screening status
      fetchScreeningStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar cliente");
      navigate("/customers");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchScreeningStatus = async () => {
    try {
      const res = await axios.get(`${API}/screening/customer/${id}/status`, { withCredentials: true });
      setScreeningStatus(res.data);
    } catch (err) {
      // Screening status endpoint may not exist or customer not screened yet
      setScreeningStatus(null);
    }
  };

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  // Poll for CURP background download result when navigating from customer creation
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get("curpPending")) return;

    // Remove the query param from the URL immediately (cosmetic)
    window.history.replaceState(null, "", location.pathname);

    const MAX_POLL_MS = 90000; // 90 seconds max
    const INTERVAL_MS = 4000;
    const started = Date.now();

    curpPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/customers/${id}`, { withCredentials: true });
        const status = res.data?.curp_pdf_status;

        if (status === "downloaded") {
          clearInterval(curpPollRef.current);
          setCustomer(res.data); // refresh with latest data (silent success)
        } else if (status === "failed") {
          clearInterval(curpPollRef.current);
          setCustomer(res.data);
          const errorMsg = res.data?.curp_pdf_error ||
            "No se pudo descargar el CURP automáticamente. Puedes subirlo manualmente desde el detalle del cliente.";
          toast.error(errorMsg, { duration: 8000 });
        } else if (Date.now() - started >= MAX_POLL_MS) {
          clearInterval(curpPollRef.current);
          // Timeout reached — stop polling silently (user can retry manually later)
        }
      } catch {
        // Ignore transient fetch errors during polling
      }
    }, INTERVAL_MS);

    return () => clearInterval(curpPollRef.current);
  }, [id, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEditing = () => {
    setForm({
      first_name: customer.first_name || "",
      paternal_surname: customer.paternal_surname || "",
      maternal_surname: customer.maternal_surname || "",
      legal_name: customer.legal_name || "",
      commercial_name: customer.commercial_name || "",
      birth_date: customer.birth_date || "",
      nationality: customer.nationality || "Mexicana",
      gender: customer.gender || "",
      occupation: customer.occupation || "",
      economic_activity: customer.economic_activity || "",
      curp: customer.curp || "",
      rfc: customer.rfc || "",
      id_type: customer.id_type || "",
      id_number: customer.id_number || "",
      id_expiry_date: customer.id_expiry_date || "",
      street: customer.street || "",
      ext_number: customer.ext_number || "",
      int_number: customer.int_number || "",
      neighborhood: customer.neighborhood || "",
      zip_code: customer.zip_code || "",
      state: customer.state || "",
      municipality: customer.municipality || "",
      phone: customer.phone || "",
      mobile: customer.mobile || "",
      email: customer.email || "",
      is_pep: customer.is_pep || false,
      pep_details: customer.pep_details || "",
    });
    setEditing(true);
    setActiveTab("info");
  };

  const cancelEditing = () => { setEditing(false); };

  const saveCustomer = async () => {
    setSaving(true);
    try {
      const cleaned = {};
      for (const [k, v] of Object.entries(form)) {
        if (typeof v === "string" && v.trim() === "") cleaned[k] = null;
        else cleaned[k] = v;
      }
      const res = await axios.put(`${API}/customers/${id}`, cleaned, { withCredentials: true });
      setCustomer(res.data);
      setEditing(false);
      toast.success("Cliente actualizado exitosamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar cliente");
    } finally {
      setSaving(false);
    }
  };

  // Run PEP/OFAC Screening (separate from KYC verification)
  //
  // Two-step async flow:
  //   1. POST /screening/customer/{id}/start  → returns {job_id} instantly
  //   2. GET  /screening/jobs/{job_id}        → poll every 2s
  //
  // The polled phase field drives the visible step indicator so the user
  // sees real backend progress instead of guessed timings. Polling stops
  // when status is "done" or "failed", or after a hard 90s wall-clock
  // limit on the client side too (defence against a stuck job leaking
  // forever).
  const handleRunScreening = async () => {
    setScreeningPhase(0);
    setScreeningLoading(true);
    let pollHandle = null;
    const startedAt = Date.now();

    // Map backend phase strings to the UI step index (kept in sync with
    // the overlay's `steps` array below).
    const PHASE_TO_STEP = {
      queued: 0,
      calling_providers: 1,
      retrying: 2,             // ← new: shown when backend triggered auto-retry
      updating_customer: 3,
      done: 4,
    };

    const onSuccess = async (result) => {
      setScreening(result);
      try {
        const statusRes = await axios.get(`${API}/screening/customer/${id}/status`, { withCredentials: true });
        setScreeningStatus(statusRes.data);
      } catch { /* non-fatal */ }
      fetchCustomer();
      toast.success("Screening PEP/OFAC completado");
      if (result.has_match) {
        toast.warning(
          `Se encontraron ${result.total_matches} coincidencia(s). Revísalas antes de continuar.`,
          { duration: 5000 },
        );
      }
    };

    const onError = (err, jobError) => {
      if (jobError) {
        toast.error(jobError, { duration: 8000 });
        return;
      }
      // ── Translate backend error into something the operator can act on.
      // The detail field can be:
      //   • a string for normal errors
      //   • {error, limit_type, current, max_allowed, message, reset_date}
      //     for the 403 monthly-limit response from services/tenant.py
      //   • {code: "monthly_limit_exceeded", needed, available} for the
      //     402 batch quota response
      //   • missing entirely for network / timeout errors
      const d = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg;
      if (typeof d === "string" && d) msg = d;
      else if (d && typeof d === "object") {
        if (d.code === "monthly_limit_exceeded") {
          msg = `No tienes suficientes screenings. Necesitas ${d.needed ?? 1}, te quedan ${d.available ?? 0}. Actualiza tu plan para continuar.`;
        } else if (d.error === "limit_exceeded") {
          msg = d.message || `Has alcanzado el límite de ${d.limit_type || "uso"} de tu plan (${d.current}/${d.max_allowed}). Actualiza tu plan.`;
        } else {
          msg = d.message || JSON.stringify(d);
        }
      } else if (status) msg = `Error ${status} al ejecutar screening`;
      else msg = err?.message || "Error al ejecutar screening";
      toast.error(msg, { duration: 8000 });
    };

    const cleanup = () => {
      if (pollHandle) clearInterval(pollHandle);
      setScreeningPhase(0);
      setScreeningLoading(false);
    };

    try {
      // 1. Start the job
      const startRes = await axios.post(
        `${API}/screening/customer/${id}/start`, {},
        { withCredentials: true },
      );
      const jobId = startRes.data.job_id;
      if (!jobId) throw new Error("El backend no devolvió job_id");

      // 2. Poll
      pollHandle = setInterval(async () => {
        try {
          // 90s wall-clock fail-safe — matches the backend's hard timeout.
          if (Date.now() - startedAt > 90000) {
            cleanup();
            toast.error(
              "El screening sigue procesándose en el servidor. Recarga la página en unos minutos para ver el resultado.",
              { duration: 10000 },
            );
            return;
          }

          const res = await axios.get(`${API}/screening/jobs/${jobId}`, { withCredentials: true });
          const job = res.data;

          // Advance UI step. Stay on phase 4 ("paciencia") if we've been
          // running for > 25s and the job hasn't moved past calling_providers.
          let step = PHASE_TO_STEP[job.phase] ?? 1;
          if (job.status === "running" && step === 1 && (Date.now() - startedAt > 25000)) {
            step = 4;
          }
          setScreeningPhase(step);

          if (job.status === "done") {
            cleanup();
            await onSuccess(job.result);
          } else if (job.status === "failed") {
            cleanup();
            onError(null, job.error || "El screening falló en el servidor");
          }
        } catch (pollErr) {
          // Transient poll failures (e.g. 502 from the ingress) shouldn't
          // kill the whole flow — keep polling. A 404 means the job got
          // wiped (TTL or manual cleanup); that one we treat as fatal.
          if (pollErr?.response?.status === 404) {
            cleanup();
            toast.error("El job de screening ya no existe en el servidor. Vuelve a intentarlo.", { duration: 6000 });
          }
        }
      }, 2000);
    } catch (err) {
      cleanup();
      onError(err, null);
    }
  };

  const handleVerifyKyc = async () => {
    // Show verification modal
    setVerifyingKyc(true);
    setVerifyProgress({ step: 1, message: "Validando requisitos...", screening: null, pdf: null });
    
    try {
      await new Promise(r => setTimeout(r, 500));
      
      setVerifyProgress({ step: 2, message: "Generando expediente KYC...", screening: screeningStatus, pdf: null });
      
      const result = await axios.post(`${API}/customers/${id}/verify-kyc`, {}, { withCredentials: true });
      
      await new Promise(r => setTimeout(r, 500));
      
      setVerifyProgress({ 
        step: 3, 
        message: "¡Verificación completada!", 
        screening: result.data.screening,
        pdf: result.data.pdf
      });
      
      setCustomer({ ...customer, kyc_status: "verified", kyc_pdf_path: result.data.pdf?.download_url });
      
      // Run risk assessment in background
      try {
        const riskRes = await axios.get(`${API}/customers/${id}/risk-assessment`, { withCredentials: true });
        setRisk(riskRes.data);
        setCustomer(prev => ({ ...prev, risk_level: riskRes.data.level }));
      } catch (err) {
        console.error("Risk assessment failed:", err);
      }
      
      // Refresh data
      fetchCustomer();
      
    } catch (err) {
      setVerifyingKyc(false);
      setVerifyProgress(null);
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.errors) {
        toast.error(detail.errors.join("\n"), { duration: 6000 });
      } else {
        toast.error(typeof detail === "string" ? detail : "Error al verificar KYC");
      }
    }
  };
  
  const handleDownloadKycPdf = async () => {
    try {
      const response = await axios.get(`${API}/customers/${id}/kyc-pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', customer.kyc_pdf_filename || `KYC_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF descargado exitosamente");
    } catch (err) {
      toast.error("Error al descargar el PDF. Intente regenerar el KYC.");
    }
  };
  
  const handleRegenerateKyc = async () => {
    try {
      setVerifyingKyc(true);
      setVerifyProgress({ step: 1, message: "Regenerando expediente KYC...", screening: null, pdf: null });
      
      // Use the generate-kyc-pdf endpoint to regenerate
      const response = await axios.get(`${API}/customers/${id}/generate-kyc-pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      setVerifyProgress({ step: 2, message: "¡PDF regenerado!", screening: null, pdf: { generated: true } });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `KYC_${id}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF regenerado y descargado exitosamente");
      
      // Refresh customer data
      fetchCustomer();
      
      setTimeout(() => {
        setVerifyingKyc(false);
        setVerifyProgress(null);
      }, 1500);
      
    } catch (err) {
      setVerifyingKyc(false);
      setVerifyProgress(null);
      toast.error(err.response?.data?.detail || "Error al regenerar el PDF");
    }
  };
  
  const closeVerifyModal = () => {
    setVerifyingKyc(false);
    setVerifyProgress(null);
  };
  
  // Open modal to dismiss a match
  const openDismissModal = (matchIndex, match) => {
    setDismissModal({ open: true, matchIndex, match, action: 'dismiss' });
    setDismissReason("");
  };
  
  // Open modal to confirm a match
  const openConfirmModal = (matchIndex, match) => {
    setDismissModal({ open: true, matchIndex, match, action: 'confirm' });
    setDismissReason("");
  };
  
  // Handle match dismissal
  const handleDismissMatch = async () => {
    if (!dismissReason.trim()) {
      toast.error("Debe proporcionar una razón para descartar la coincidencia");
      return;
    }
    
    try {
      await axios.post(
        `${API}/screening/${screeningStatus.screening_id}/dismiss-match`,
        { match_index: dismissModal.matchIndex, reason: dismissReason },
        { withCredentials: true }
      );
      toast.success("Coincidencia descartada");
      setDismissModal({ open: false, matchIndex: null, match: null, action: 'dismiss' });
      setDismissReason("");
      
      // Refresh screening status immediately
      const statusRes = await axios.get(`${API}/screening/customer/${id}/status`, { withCredentials: true });
      setScreeningStatus(statusRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al descartar coincidencia");
    }
  };
  
  // Handle match confirmation
  const handleConfirmMatch = async () => {
    try {
      await axios.post(
        `${API}/screening/${screeningStatus.screening_id}/confirm-match`,
        { match_index: dismissModal.matchIndex, notes: dismissReason },
        { withCredentials: true }
      );
      toast.success("Coincidencia confirmada - Se ha creado una alerta de riesgo");
      setDismissModal({ open: false, matchIndex: null, match: null, action: 'confirm' });
      setDismissReason("");
      
      // Refresh screening status immediately
      const statusRes = await axios.get(`${API}/screening/customer/${id}/status`, { withCredentials: true });
      setScreeningStatus(statusRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al confirmar coincidencia");
    }
  };

  const handleRiskAssessment = async () => {
    try {
      const res = await axios.get(`${API}/customers/${id}/risk-assessment`, { withCredentials: true });
      setRisk(res.data);
      setCustomer({ ...customer, risk_level: res.data.level });
      toast.success("Evaluacion de riesgo completada");
    } catch { toast.error("Error en evaluacion de riesgo"); }
  };

  // Old functions removed - new ones defined above (handleRunScreening, openDismissModal, etc.)



  const handleDeleteCustomer = async () => {
    const name = customer?.person_type === "moral" 
      ? customer?.legal_name 
      : [customer?.first_name, customer?.paternal_surname, customer?.maternal_surname].filter(Boolean).join(" ");
    
    if (!window.confirm(`¿Estás seguro de eliminar al cliente "${name || customer?.customer_number}"?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/customers/${id}`, { withCredentials: true });
      toast.success("Cliente eliminado exitosamente");
      navigate("/customers");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar cliente");
    }
  };

  if (loading) return (
    <Layout><div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
    </div></Layout>
  );

  if (!customer) return null;

  const kyc = kycBadge[customer.kyc_status] || kycBadge.pending;
  const riskInfo = riskBadge[customer.risk_level] || riskBadge.low;
  const KycIcon = kyc.icon;
  const isMoral = customer.person_type === "moral";
  const displayName = isMoral
    ? customer.legal_name
    : [customer.first_name, customer.paternal_surname, customer.maternal_surname].filter(Boolean).join(" ");
  const completeness = computeCompleteness(customer);
  const isIncomplete = completeness.pct < 100;
  const isVerified = customer.kyc_status === "verified";
  const fc = "bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500";
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  return (
    <Layout>
      <div data-testid="customer-detail-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/customers")} className="text-slate-400 hover:text-white" data-testid="back-button">
            <ArrowLeft size={18} />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-bold text-white" data-testid="customer-name">{displayName || "Cliente sin nombre"}</h1>
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs ${kyc.cls}`}>
                <KycIcon size={12} /> {kyc.label}
              </span>
              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs ${riskInfo.cls}`}>
                Riesgo: {riskInfo.label}
              </span>
              {isMoral && (
                <span className="inline-flex px-2.5 py-1 rounded-full text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20">
                  Persona Moral
                </span>
              )}
            </div>
            <p className="text-slate-400 text-sm mt-0.5 font-mono">{customer.customer_number}</p>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            {!editing && !isVerified && (
              <Button onClick={startEditing} className={isIncomplete
                ? "bg-amber-600 hover:bg-amber-500 text-white"
                : "bg-slate-700 hover:bg-slate-600 text-white"
              } data-testid="edit-customer-btn">
                <Pencil size={14} className="mr-2" />
                {isIncomplete ? "Completar Registro" : "Editar Cliente"}
              </Button>
            )}
            {!editing && isVerified && (
              <>
                <Button onClick={startEditing} className="bg-slate-700 hover:bg-slate-600 text-white" data-testid="edit-customer-btn">
                  <Pencil size={14} className="mr-2" /> Editar Cliente
                </Button>
              </>
            )}
            {editing && (
              <>
                <Button onClick={cancelEditing} variant="outline" className="border-slate-700 text-slate-400" data-testid="cancel-edit-btn">
                  <X size={14} className="mr-2" /> Cancelar
                </Button>
                <Button onClick={saveCustomer} disabled={saving} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="save-edit-btn">
                  <Save size={14} className="mr-2" /> {saving ? "Guardando..." : "Guardar Cambios"}
                </Button>
              </>
            )}
            
            {/* Phase 1: Show Screening button when not verified and screening not done */}
            {!editing && !isVerified && !screeningStatus?.screening_done && (
              <Button 
                onClick={handleRunScreening} 
                disabled={screeningLoading}
                className="bg-blue-600 hover:bg-blue-500 text-white" 
                data-testid="run-screening-button"
              >
                {screeningLoading ? (
                  <><Loader2 size={14} className="mr-2 animate-spin" /> Ejecutando...</>
                ) : (
                  <><Search size={14} className="mr-2" /> Ejecutar Screening PEP/OFAC</>
                )}
              </Button>
            )}
            
            {/* Phase 2: Show Verify KYC button when screening done and all matches resolved */}
            {!editing && !isVerified && screeningStatus?.screening_done && screeningStatus?.can_verify_kyc && (
              <Button onClick={handleVerifyKyc} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="verify-kyc-button">
                <CheckCircle size={14} className="mr-2" /> Verificar KYC
              </Button>
            )}
            
            {/* Phase 3: Show Download and Regenerate buttons when verified */}
            {!editing && isVerified && (
              <>
                <Button onClick={handleDownloadKycPdf} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="download-kyc-pdf-button">
                  <Download size={14} className="mr-2" /> Descargar KYC
                </Button>
                <Button onClick={handleRegenerateKyc} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="regenerate-kyc-button">
                  <RefreshCw size={14} className="mr-2" /> Regenerar KYC
                </Button>
              </>
            )}
            
            {!editing && (
              <Button onClick={handleDeleteCustomer} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid="delete-customer-button">
                <Trash2 size={14} className="mr-2" /> Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* Screening progress overlay — shows phased progress during the
            potentially long (5-30s) PEP/OFAC call so the user isn't staring
            at an opaque spinner. Phases advance on timers because the
            backend doesn't stream progress; the timings reflect production
            metrics (cold cache ~25s, warm ~1-3s). */}
        {screeningLoading && (
          <div
            data-testid="screening-progress-overlay"
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4"
          >
            <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-lg w-full p-7 shadow-2xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 bg-blue-500/10 rounded-lg">
                  <ShieldCheck className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Ejecutando screening</h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Esto puede tardar entre 2 y 30 segundos. Por favor no cierres la ventana.
                  </p>
                </div>
              </div>

              {/* Phase steps */}
              <div className="space-y-2.5">
                {[
                  { label: "Validando datos del cliente" },
                  { label: "Consultando OpenSanctions + OFAC SDN" },
                  { label: "Reintentando consulta (proveedor inestable)…", retry: true },
                  { label: "Procesando coincidencias y clasificando riesgo" },
                ].map((step, idx) => {
                  const done = screeningPhase > idx;
                  const active = screeningPhase === idx;
                  // The retry step only renders when actually active or
                  // when we passed it (otherwise we shouldn't draw it as
                  // "pending" — most jobs never need a retry).
                  if (step.retry && !active && !done) return null;
                  return (
                    <div
                      key={idx}
                      data-testid={`screening-phase-${idx}`}
                      className={`flex items-center gap-3 text-sm transition-colors ${
                        step.retry && active
                          ? "text-amber-300"
                          : done
                            ? "text-emerald-300"
                            : active
                              ? "text-blue-300"
                              : "text-slate-500"
                      }`}
                    >
                      <span className="w-5 h-5 flex items-center justify-center shrink-0">
                        {done ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : active ? (
                          step.retry ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          )
                        ) : (
                          <CircleDot className="w-3.5 h-3.5 opacity-50" />
                        )}
                      </span>
                      <span>{step.label}</span>
                    </div>
                  );
                })}
              </div>

              {/* Patience banner — only after phase 4 (≥25s). Avoids alarming
                  users who get a fast warm-cache response. */}
              {screeningPhase >= 4 && (
                <div
                  data-testid="screening-patience-banner"
                  className="mt-5 px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3"
                >
                  <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-300 leading-relaxed">
                    El proveedor está tardando un poco más de lo normal en responder.
                    Sigue procesando — no abandones la ventana. Si nunca termina,
                    revisa <a
                      href="/superadmin/system-status"
                      className="underline hover:text-amber-200"
                    >Servicios externos</a> y vuelve a intentarlo.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Completeness Banner */}
        {isIncomplete && !editing && !isVerified && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-5 py-3 mb-4 flex items-center justify-between" data-testid="completeness-banner">
            <div className="flex items-center gap-3">
              <CircleDot size={18} className="text-amber-400" />
              <div>
                <p className="text-sm font-medium text-amber-300">Registro incompleto — {completeness.pct}% completado ({completeness.filled}/{completeness.total} campos)</p>
                <p className="text-xs text-amber-400/70 mt-0.5">
                  Falta: {completeness.missing.map(f => FIELD_LABELS[f] || f).join(", ")}
                </p>
              </div>
            </div>
            <Button onClick={startEditing} size="sm" className="bg-amber-600 hover:bg-amber-500 text-white shrink-0" data-testid="complete-onboarding-btn">
              Completar Registro
            </Button>
          </div>
        )}

        {/* Onboarding lifecycle hint for persona moral */}
        {isMoral && !isVerified && !editing && (
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl px-5 py-3 mb-4" data-testid="moral-onboarding-hint">
            <p className="text-sm text-blue-300 flex items-center gap-2">
              <Briefcase size={16} className="shrink-0" />
              <span>
                <strong>Persona Moral:</strong> Para verificar el KYC se requiere: datos completos de la empresa, al menos un representante legal registrado, y documentacion corporativa.
              </span>
            </p>
          </div>
        )}

        {/* Risk Assessment Result */}
        {risk && !editing && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-4" data-testid="risk-result">
            <h3 className="text-sm font-semibold text-white mb-3">Evaluacion de Riesgo</h3>
            <div className="flex items-center gap-4 mb-2">
              <div className="text-2xl font-bold text-white">{risk.score}/100</div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${(riskBadge[risk.level] || riskBadge.low).cls}`}>
                {(riskBadge[risk.level] || riskBadge.low).label}
              </span>
            </div>
            {risk.factors.length > 0 && (
              <ul className="space-y-1">
                {risk.factors.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-400">
                    <AlertTriangle size={12} className="text-amber-400" /> {f}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Screening Status and Matches Resolution */}
        {screeningStatus?.screening_done && !editing && !isVerified && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-4" data-testid="screening-status">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white">Screening PEP/OFAC</h3>
              <div className="text-xs text-slate-500">
                {screeningStatus.screened_at && new Date(screeningStatus.screened_at).toLocaleString("es-MX", { 
                  dateStyle: "short", 
                  timeStyle: "short"
                })}
              </div>
            </div>
            
            <div className="flex items-center gap-3 mb-3">
              {screeningStatus.total_matches > 0 ? (
                <ShieldAlert size={22} className={screeningStatus.all_resolved ? "text-amber-400" : "text-red-400"} />
              ) : (
                <ShieldCheck size={22} className="text-emerald-400" />
              )}
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                screeningStatus.risk_classification === "critical" ? "bg-red-500/10 text-red-400" :
                screeningStatus.risk_classification === "high" ? "bg-orange-500/10 text-orange-400" :
                screeningStatus.risk_classification === "medium" ? "bg-amber-500/10 text-amber-400" :
                "bg-emerald-500/10 text-emerald-400"
              }`}>
                {screeningStatus.total_matches === 0 ? "Sin coincidencias" :
                 screeningStatus.all_resolved ? "Coincidencias resueltas" :
                 `${screeningStatus.unresolved_matches} pendiente(s) de resolver`}
              </span>
            </div>
            
            {/* Summary badges */}
            {screeningStatus.total_matches > 0 && (
              <div className="flex gap-2 mb-3 flex-wrap">
                {screeningStatus.dismissed_count > 0 && (
                  <span className="px-2 py-1 rounded bg-slate-800 text-xs text-slate-400">
                    {screeningStatus.dismissed_count} descartada(s)
                  </span>
                )}
                {screeningStatus.confirmed_count > 0 && (
                  <span className="px-2 py-1 rounded bg-red-500/10 text-xs text-red-400">
                    {screeningStatus.confirmed_count} confirmada(s)
                  </span>
                )}
                {screeningStatus.unresolved_matches > 0 && (
                  <span className="px-2 py-1 rounded bg-amber-500/10 text-xs text-amber-400">
                    {screeningStatus.unresolved_matches} sin resolver
                  </span>
                )}
              </div>
            )}
            
            {/* Message for status */}
            {!screeningStatus.can_verify_kyc && screeningStatus.total_matches > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-2 text-sm text-amber-300">
                  <AlertTriangle size={14} />
                  <span>Debe resolver todas las coincidencias antes de verificar el KYC</span>
                </div>
              </div>
            )}
            
            {screeningStatus.can_verify_kyc && screeningStatus.total_matches === 0 && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-2 text-sm text-emerald-300">
                  <CheckCircle size={14} />
                  <span>Screening completado sin coincidencias. Puede proceder con la verificación KYC.</span>
                </div>
              </div>
            )}
            
            {screeningStatus.can_verify_kyc && screeningStatus.total_matches > 0 && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-2 text-sm text-emerald-300">
                  <CheckCircle size={14} />
                  <span>Todas las coincidencias han sido resueltas. Puede proceder con la verificación KYC.</span>
                </div>
              </div>
            )}
            
            {/* Match list */}
            {screeningStatus.matches?.length > 0 && screeningStatus.matches.map((m, i) => {
              const isDismissed = m.dismissed_info?.dismissed;
              const isConfirmed = m.confirmed;
              const isResolved = isDismissed || isConfirmed;
              
              return (
                <div key={i} className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 mt-2 ${
                  isDismissed ? "bg-slate-800/20 opacity-60" : 
                  isConfirmed ? "bg-red-500/10 border border-red-500/20" :
                  "bg-amber-500/10 border border-amber-500/20"
                }`}>
                  {isDismissed ? (
                    <CheckCircle size={14} className="text-slate-500 shrink-0" />
                  ) : isConfirmed ? (
                    <ShieldAlert size={14} className="text-red-400 shrink-0" />
                  ) : (
                    <AlertTriangle size={14} className="text-amber-400 shrink-0" />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={isDismissed ? "text-slate-500 line-through" : isConfirmed ? "text-red-300" : "text-slate-200"}>
                        {m.matched_name}
                      </span>
                      <span className="text-xs text-slate-500">
                        ({m.list}) {m.confidence}% similitud
                      </span>
                    </div>
                    {isDismissed && m.dismissed_info?.dismissal_reason && (
                      <div className="text-xs text-slate-500 mt-1">
                        <span className="text-slate-600">Descartado:</span> {m.dismissed_info.dismissal_reason}
                      </div>
                    )}
                    {isConfirmed && m.confirmed_info?.confirmation_notes && (
                      <div className="text-xs text-red-400 mt-1">
                        <span className="text-red-500">Confirmado:</span> {m.confirmed_info.confirmation_notes}
                      </div>
                    )}
                  </div>
                  {!isResolved && (
                    <div className="flex gap-1 shrink-0">
                      <button
                        onClick={() => openDismissModal(i, m)}
                        className="text-xs px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors whitespace-nowrap"
                      >
                        Descartar
                      </button>
                      <button
                        onClick={() => openConfirmModal(i, m)}
                        className="text-xs px-2 py-1 rounded bg-red-600/80 hover:bg-red-600 text-white transition-colors whitespace-nowrap"
                      >
                        Confirmar
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {/* Show screening summary for verified customers */}
        {screening && isVerified && !editing && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-4" data-testid="screening-result">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white">Screening PEP/OFAC</h3>
              <div className="text-xs text-slate-500">
                {new Date(screening.screened_at).toLocaleString("es-MX", { 
                  dateStyle: "short", 
                  timeStyle: "medium",
                  timeZone: screening.timezone || "UTC"
                })} {screening.timezone || "UTC"}
              </div>
            </div>
            
            <div className="flex items-center gap-3 mb-3">
              {screening.has_match ? <ShieldAlert size={22} className="text-amber-400" /> : <ShieldCheck size={22} className="text-emerald-400" />}
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                screening.risk_classification === "critical" ? "bg-red-500/10 text-red-400" :
                screening.risk_classification === "high" ? "bg-orange-500/10 text-orange-400" :
                screening.risk_classification === "medium" ? "bg-amber-500/10 text-amber-400" :
                "bg-emerald-500/10 text-emerald-400"
              }`}>
                {screening.risk_classification === "clear" ? "Sin coincidencias" : "Revisado"}
              </span>
            </div>
          </div>
        )}

        {/* CURP Validation Status */}
        {customer.person_type === "fisica" && !editing && customer.curp && 
         customer.curp_pdf_status !== 'pending' && 
         customer.curp_pdf_status !== 'downloading' && (
          <CurpStatusCard
            customer={customer}
            onRetry={async (onFailure) => {
              try {
                toast.info("Descargando constancia CURP desde gob.mx...", { duration: 10000 });
                const res = await axios.post(`${API}/curp/download-pdf/${customer.customer_id}`, {}, { withCredentials: true });
                if (res.data.success) {
                  toast.success("¡Constancia CURP descargada exitosamente!");
                  fetchCustomer();
                } else if (res.data.error_type === "service_unavailable") {
                  // Let the card show its own detailed UI — don't use toast
                  onFailure?.(res.data);
                } else {
                  toast.error(res.data.error || "No se pudo descargar la CURP");
                  onFailure?.(res.data);
                }
              } catch (err) {
                const errorMsg = err.response?.data?.detail || err.response?.data?.error || "No se pudo conectar con gob.mx";
                toast.error(errorMsg);
              }
            }}
          />
        )}


        {/* Tabbed Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-900 border border-slate-800 mb-4">
            <TabsTrigger value="info" data-testid="tab-info">
              <FileText size={14} className="mr-2" /> {editing ? "Editar Datos" : "Informacion"}
            </TabsTrigger>
            {isMoral && (
              <TabsTrigger value="representatives" data-testid="tab-representatives" disabled={editing}>
                <Briefcase size={14} className="mr-2" /> Sujetos del Expediente
              </TabsTrigger>
            )}
            <TabsTrigger value="documents" data-testid="tab-documents" disabled={editing}>
              <Upload size={14} className="mr-2" /> Documentos
            </TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance" disabled={editing}>
              <Shield size={14} className="mr-2" /> Cumplimiento
            </TabsTrigger>
            <TabsTrigger value="operations" data-testid="tab-operations" disabled={editing}>
              <ArrowLeftRight size={14} className="mr-2" /> Operaciones ({operations.length})
            </TabsTrigger>
          </TabsList>

          {/* Info Tab — View or Edit Mode */}
          <TabsContent value="info">
            {editing ? (
              <EditForm customer={customer} form={form} handleChange={handleChange} setForm={setForm} fc={fc} />
            ) : (
              <ViewInfo customer={customer} owners={owners} />
            )}
          </TabsContent>

          {/* Representatives Tab (Persona Moral only) */}
          {isMoral && (
            <TabsContent value="representatives">
              <div className="space-y-6">
                <LegalRepresentatives customerId={id} readOnly={isVerified} />
                <BeneficialOwnersList owners={owners} onRefresh={fetchCustomer} />
              </div>
            </TabsContent>
          )}

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div data-testid="customer-documents-section">
              <DocumentManager customerId={id} personType={customer.person_type} />
            </div>
          </TabsContent>

          {/* Operations Tab */}
          <TabsContent value="operations">
            <div className="bg-slate-900 border border-slate-800 rounded-xl" data-testid="customer-operations">
              <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">Operaciones del Cliente</h3>
                <span className="text-xs text-slate-500">{operations.length}</span>
              </div>
              {operations.length === 0 ? (
                <div className="px-5 py-8 text-center text-slate-500 text-sm">No hay operaciones registradas</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-800 text-left">
                        <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">No.</th>
                        <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Tipo</th>
                        <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Monto</th>
                        <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Estado</th>
                        <th className="px-5 py-2.5 text-xs font-medium text-slate-400 uppercase">Fecha</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {operations.map((op) => (
                        <tr key={op.operation_id} className="hover:bg-slate-800/50">
                          <td className="px-5 py-2.5 text-sm text-teal-400 font-mono">{op.operation_number}</td>
                          <td className="px-5 py-2.5 text-sm text-slate-300">{op.operation_type}</td>
                          <td className="px-5 py-2.5 text-sm text-slate-200 font-mono">
                            ${Number(op.amount_mxn).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-5 py-2.5">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              op.status === "approved" ? "bg-emerald-500/10 text-emerald-400" :
                              op.status === "flagged" ? "bg-red-500/10 text-red-400" :
                              "bg-slate-700 text-slate-400"
                            }`}>{op.status}</span>
                          </td>
                          <td className="px-5 py-2.5 text-sm text-slate-500">{op.operation_date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Compliance Readiness Tab */}
          <TabsContent value="compliance">
            <ComplianceReadiness customerId={id} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Dismiss/Confirm Match Modal */}
      {dismissModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              {dismissModal.action === 'dismiss' ? 'Descartar Coincidencia' : 'Confirmar Coincidencia'}
            </h3>
            <p className="text-sm text-slate-400 mb-4">
              {dismissModal.action === 'dismiss' 
                ? '¿Estás seguro de descartar esta coincidencia como falso positivo?' 
                : '¿Confirmas que esta coincidencia es real y corresponde al cliente?'}
            </p>
            <div className={`rounded-lg p-3 mb-4 ${
              dismissModal.action === 'confirm' ? 'bg-red-500/10 border border-red-500/20' : 'bg-slate-800/50'
            }`}>
              <div className="text-sm text-slate-300 mb-1">
                <strong>Match:</strong> {dismissModal.match?.matched_name}
              </div>
              <div className="text-xs text-slate-400">
                <strong>Similitud:</strong> {dismissModal.match?.confidence}% • <strong>Fuente:</strong> {dismissModal.match?.list}
              </div>
            </div>
            
            {dismissModal.action === 'confirm' && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 text-sm text-amber-300">
                  <AlertTriangle size={14} />
                  <span>Al confirmar, se creará una alerta de riesgo que deberá ser revisada.</span>
                </div>
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm text-slate-300 mb-2">
                {dismissModal.action === 'dismiss' ? 'Razón del descarte *' : 'Notas de confirmación (opcional)'}
              </label>
              <textarea
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                rows={3}
                placeholder={dismissModal.action === 'dismiss' 
                  ? "Ej: Homonimia - RFC y fecha de nacimiento no coinciden" 
                  : "Ej: Cliente confirmó ser la misma persona listada"}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDismissModal({ open: false, matchIndex: null, match: null, action: 'dismiss' })}
                className="flex-1 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm"
              >
                Cancelar
              </button>
              {dismissModal.action === 'dismiss' ? (
                <button
                  onClick={handleDismissMatch}
                  className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm"
                >
                  Confirmar Descarte
                </button>
              ) : (
                <button
                  onClick={handleConfirmMatch}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm"
                >
                  Confirmar Coincidencia
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* KYC Verification Progress Modal */}
      {verifyingKyc && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 max-w-lg w-full mx-4">
            <div className="text-center mb-6">
              <Shield className="w-12 h-12 text-teal-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white">Verificación KYC</h3>
            </div>
            
            {/* Progress Steps */}
            <div className="space-y-4 mb-6">
              {[
                { step: 1, label: "Validando requisitos" },
                { step: 2, label: "Generando expediente KYC" },
                { step: 3, label: "Verificación completada" },
              ].map(({ step, label }) => (
                <div key={step} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    verifyProgress?.step > step ? "bg-emerald-500" :
                    verifyProgress?.step === step ? "bg-teal-500" : "bg-slate-700"
                  }`}>
                    {verifyProgress?.step > step ? (
                      <CheckCircle size={16} className="text-white" />
                    ) : verifyProgress?.step === step ? (
                      <Loader2 size={16} className="text-white animate-spin" />
                    ) : (
                      <span className="text-slate-400 text-sm">{step}</span>
                    )}
                  </div>
                  <span className={`text-sm ${
                    verifyProgress?.step >= step ? "text-white" : "text-slate-500"
                  }`}>{label}</span>
                </div>
              ))}
            </div>
            
            {/* Screening Summary (from previous step) */}
            {verifyProgress?.screening && (
              <div className={`rounded-lg p-4 mb-4 ${
                verifyProgress.screening.active_matches > 0 
                  ? "bg-amber-500/10 border border-amber-500/30" 
                  : "bg-emerald-500/10 border border-emerald-500/30"
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {verifyProgress.screening.active_matches > 0 ? (
                    <AlertTriangle size={18} className="text-amber-400" />
                  ) : (
                    <ShieldCheck size={18} className="text-emerald-400" />
                  )}
                  <span className="font-medium text-white">Resultado del Screening</span>
                </div>
                <p className={`text-sm ${verifyProgress.screening.active_matches > 0 ? "text-amber-300" : "text-emerald-300"}`}>
                  {verifyProgress.screening.total_matches === 0 
                    ? "Sin coincidencias en listas PEP/OFAC"
                    : `${verifyProgress.screening.total_matches} coincidencia(s) - ${verifyProgress.screening.dismissed_matches} descartada(s)`
                  }
                </p>
              </div>
            )}
            
            {/* PDF Download */}
            {verifyProgress?.pdf?.generated && (
              <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText size={20} className="text-teal-400" />
                    <div>
                      <p className="text-sm font-medium text-white">Expediente KYC generado</p>
                      <p className="text-xs text-slate-400">{verifyProgress.pdf.filename} • {Math.round(verifyProgress.pdf.size / 1024)} KB</p>
                    </div>
                  </div>
                  <Button 
                    onClick={handleDownloadKycPdf}
                    className="bg-teal-600 hover:bg-teal-500 text-white"
                    size="sm"
                  >
                    <Download size={14} className="mr-2" /> Descargar
                  </Button>
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex justify-end gap-3">
              {verifyProgress?.step === 3 ? (
                <Button onClick={closeVerifyModal} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                  <CheckCircle size={14} className="mr-2" /> Completado
                </Button>
              ) : (
                <div className="text-sm text-slate-400 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  {verifyProgress?.message || "Procesando..."}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}

/* ── Edit Form Sub-component ── */
function EditForm({ customer, form, handleChange, setForm, fc }) {
  return (
    <div className="space-y-5" data-testid="edit-form">
      {/* Identity */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-white mb-4">
          {customer.person_type === "fisica" ? "Datos Personales" : "Datos de la Empresa"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {customer.person_type === "fisica" ? (
            <>
              <div><Label className="text-slate-400 text-xs">Nombre(s) *</Label>
                <Input name="first_name" value={form.first_name} onChange={handleChange} className={fc} data-testid="edit-first-name" /></div>
              <div><Label className="text-slate-400 text-xs">Apellido Paterno *</Label>
                <Input name="paternal_surname" value={form.paternal_surname} onChange={handleChange} className={fc} data-testid="edit-paternal-surname" /></div>
              <div><Label className="text-slate-400 text-xs">Apellido Materno</Label>
                <Input name="maternal_surname" value={form.maternal_surname} onChange={handleChange} className={fc} data-testid="edit-maternal-surname" /></div>
              <div><Label className="text-slate-400 text-xs">Fecha Nacimiento *</Label>
                <Input type="date" name="birth_date" value={form.birth_date} onChange={handleChange} className={fc} data-testid="edit-birth-date" /></div>
              <div><Label className="text-slate-400 text-xs">Nacionalidad *</Label>
                <Input name="nationality" value={form.nationality} onChange={handleChange} className={fc} data-testid="edit-nationality" /></div>
              <div><Label className="text-slate-400 text-xs">Genero</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger className={fc} data-testid="edit-gender"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="M">Masculino</SelectItem>
                    <SelectItem value="F">Femenino</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-slate-400 text-xs">Ocupacion</Label>
                <Input name="occupation" value={form.occupation} onChange={handleChange} className={fc} data-testid="edit-occupation" /></div>
            </>
          ) : (
            <>
              <div className="md:col-span-2"><Label className="text-slate-400 text-xs">Razon Social *</Label>
                <Input name="legal_name" value={form.legal_name} onChange={handleChange} className={fc} data-testid="edit-legal-name" /></div>
              <div><Label className="text-slate-400 text-xs">Nombre Comercial</Label>
                <Input name="commercial_name" value={form.commercial_name} onChange={handleChange} className={fc} data-testid="edit-commercial-name" /></div>
              <div><Label className="text-slate-400 text-xs">Actividad Economica</Label>
                <Input name="economic_activity" value={form.economic_activity} onChange={handleChange} className={fc} data-testid="edit-economic-activity" /></div>
              <div><Label className="text-slate-400 text-xs">Nacionalidad</Label>
                <Input name="nationality" value={form.nationality} onChange={handleChange} className={fc} data-testid="edit-nationality" /></div>
            </>
          )}
        </div>
      </div>

      {/* Tax & ID */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-white mb-4">Identificacion Fiscal</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><Label className="text-slate-400 text-xs">RFC *</Label>
            <Input name="rfc" value={form.rfc} onChange={handleChange} placeholder="XXXX000000XXX" className={fc} data-testid="edit-rfc" /></div>
          {customer.person_type === "fisica" && (
            <div><Label className="text-slate-400 text-xs">CURP *</Label>
              <Input name="curp" value={form.curp} onChange={handleChange} placeholder="XXXX000000XXXXXX00" className={fc} data-testid="edit-curp" /></div>
          )}
          <div><Label className="text-slate-400 text-xs">Tipo de Identificacion {customer.person_type === "fisica" ? "*" : ""}</Label>
            <Select value={form.id_type} onValueChange={(v) => setForm({ ...form, id_type: v })}>
              <SelectTrigger className={fc} data-testid="edit-id-type"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="ine">INE/IFE</SelectItem>
                <SelectItem value="passport">Pasaporte</SelectItem>
                <SelectItem value="cedula">Cedula Profesional</SelectItem>
                <SelectItem value="fm3">FM3</SelectItem>
                {customer.person_type === "moral" && <SelectItem value="acta_constitutiva">Acta Constitutiva</SelectItem>}
              </SelectContent>
            </Select></div>
          <div><Label className="text-slate-400 text-xs">No. de Identificacion {customer.person_type === "fisica" ? "*" : ""}</Label>
            <Input name="id_number" value={form.id_number} onChange={handleChange} className={fc} data-testid="edit-id-number" /></div>
          <div><Label className="text-slate-400 text-xs">Vigencia Identificacion</Label>
            <Input type="date" name="id_expiry_date" value={form.id_expiry_date} onChange={handleChange} className={fc} data-testid="edit-id-expiry" /></div>
        </div>
      </div>

      {/* Address */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-white mb-4">Domicilio</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="md:col-span-2"><Label className="text-slate-400 text-xs">Calle *</Label>
            <Input name="street" value={form.street} onChange={handleChange} className={fc} data-testid="edit-street" /></div>
          <div><Label className="text-slate-400 text-xs">No. Exterior</Label>
            <Input name="ext_number" value={form.ext_number} onChange={handleChange} className={fc} /></div>
          <div><Label className="text-slate-400 text-xs">No. Interior</Label>
            <Input name="int_number" value={form.int_number} onChange={handleChange} className={fc} /></div>
          <div><Label className="text-slate-400 text-xs">Colonia</Label>
            <Input name="neighborhood" value={form.neighborhood} onChange={handleChange} className={fc} /></div>
          <div><Label className="text-slate-400 text-xs">C.P.</Label>
            <Input name="zip_code" value={form.zip_code} onChange={handleChange} className={fc} /></div>
          <div><Label className="text-slate-400 text-xs">Estado *</Label>
            <Input name="state" value={form.state} onChange={handleChange} className={fc} data-testid="edit-state" /></div>
          <div><Label className="text-slate-400 text-xs">Municipio *</Label>
            <Input name="municipality" value={form.municipality} onChange={handleChange} className={fc} data-testid="edit-municipality" /></div>
        </div>
      </div>

      {/* Contact */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-white mb-4">Contacto</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label className="text-slate-400 text-xs">Telefono *</Label>
            <Input name="phone" value={form.phone} onChange={handleChange} className={fc} data-testid="edit-phone" /></div>
          <div><Label className="text-slate-400 text-xs">Celular</Label>
            <Input name="mobile" value={form.mobile} onChange={handleChange} className={fc} /></div>
          <div><Label className="text-slate-400 text-xs">Email *</Label>
            <Input type="email" name="email" value={form.email} onChange={handleChange} className={fc} data-testid="edit-email" /></div>
        </div>
      </div>

      {/* PEP */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-white mb-4">PEP</h2>
        <div className="flex items-center gap-3 mb-3">
          <input type="checkbox" id="edit_pep" checked={form.is_pep} onChange={(e) => setForm({ ...form, is_pep: e.target.checked })}
            className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-teal-500" data-testid="edit-is-pep" />
          <Label htmlFor="edit_pep" className="text-slate-300 text-sm">Persona Politicamente Expuesta</Label>
        </div>
        {form.is_pep && (
          <Input name="pep_details" value={form.pep_details} onChange={handleChange} placeholder="Cargo o funcion publica" className={fc} data-testid="edit-pep-details" />
        )}
      </div>
    </div>
  );
}

/* ── View Info Sub-component ── */
function ViewInfo({ customer, owners }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="customer-info">
        <h3 className="text-sm font-semibold text-white mb-4">Informacion General</h3>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          {[
            ["Tipo", customer.person_type === "moral" ? "Persona Moral" : "Persona Fisica"],
            ...(customer.person_type === "moral" ? [
              ["Razon Social", customer.legal_name],
              ["Nombre Comercial", customer.commercial_name],
              ["Actividad Economica", customer.economic_activity],
            ] : []),
            ["RFC", customer.rfc],
            ...(customer.person_type === "fisica" ? [
              ["CURP", customer.curp + (customer.curp_validated ? " ✅ Validada RENAPO" : "")]
            ] : []),
            ["Nacimiento", customer.birth_date],
            ["Nacionalidad", customer.nationality],
            ["Identificacion", customer.id_type ? `${customer.id_type} — ${customer.id_number || ""}` : null],
            ["Domicilio", [customer.street, customer.ext_number, customer.neighborhood, customer.municipality, customer.state].filter(Boolean).join(", ") || null],
            ["C.P.", customer.zip_code],
            ["Telefono", customer.phone || customer.mobile],
            ["Email", customer.email],
            ["PEP", customer.is_pep ? `SI — ${customer.pep_details || ""}` : "NO"],
          ].map(([label, value]) => {
            const isMissing = !value || value === "NO";
            return (
              <div key={label}>
                <dt className="text-slate-500">{label}</dt>
                <dd className={`mt-0.5 ${isMissing && label !== "PEP" ? "text-slate-600 italic" : "text-slate-200"}`}>
                  {value || "Sin dato"}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>

      {/* Propietarios Reales section removed per user request */}
    </div>
  );
}
